import React, { useEffect, useMemo, useState } from 'react';
import { X, Check } from 'lucide-react';
import { GlassButton } from '../ui';
import {
  createSitePage, listSiteBundlePages, getNavCandidates,
  ImportedPage, NavCandidate,
} from '../../services/api';

interface Props {
  siteId: string;
  onClose: () => void;
  onCreated: (page: ImportedPage) => void;
  /** Disables the "Blog index" kind — the backend allows only one per site (400 otherwise). */
  hasBlogIndex: boolean;
}

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

const input: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box',
  borderRadius: 'var(--radius-sm)', border: '1px solid var(--stroke)',
  background: 'var(--panel)', color: 'var(--text)',
};
const label: React.CSSProperties = {
  display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, marginTop: 12,
};

/**
 * Create a page that does not exist in the imported bundle. It is synthesised at serve time from a
 * "chrome donor" — an existing bundle page whose <head>, nav and footer it clones — so it inherits
 * the site's own fonts, palette and logo instead of looking like an AIME page.
 *
 * Styling matches ImportedSiteEditor's inline-style + CSS-variable convention (that file has no
 * stylesheet); never hardcode colours.
 */
const NewPageModal: React.FC<Props> = ({ siteId, onClose, onCreated, hasBlogIndex }) => {
  const [title, setTitle] = useState('');
  const [pageRel, setPageRel] = useState('');
  const [relTouched, setRelTouched] = useState(false);
  // 'blog_index' is disabled in the <select> below whenever hasBlogIndex is true (a site can
  // only have one blog index), so 'landing' is always a safe default regardless of hasBlogIndex.
  const [kind, setKind] = useState<'landing' | 'blog_index'>('landing');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [donor, setDonor] = useState('');
  const [bundlePages, setBundlePages] = useState<string[]>([]);
  const [addNavLink, setAddNavLink] = useState(true);
  const [navLabel, setNavLabel] = useState('');
  const [includeScripts, setIncludeScripts] = useState(true);
  const [navSelector, setNavSelector] = useState('');
  const [candidates, setCandidates] = useState<NavCandidate[]>([]);
  const [showNavPicker, setShowNavPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    listSiteBundlePages(siteId)
      .then(r => { setBundlePages(r.pages || []); setDonor(r.entrypoint || (r.pages || [])[0] || ''); })
      .catch(() => setError('Could not read the bundle’s pages — reload and try again.'));
  }, [siteId]);

  // The nav picker is only meaningful once we know which donor's nav we're cloning.
  useEffect(() => {
    if (!showNavPicker || !donor) return;
    getNavCandidates(siteId, donor)
      .then(r => setCandidates(r.candidates || []))
      .catch(() => setCandidates([]));
  }, [showNavPicker, donor, siteId]);

  // The URL tracks the title until the user edits it themselves — then we stop fighting them.
  const effectiveRel = useMemo(
    () => (relTouched ? pageRel : (title ? `${slugify(title)}.html` : '')),
    [relTouched, pageRel, title],
  );

  const submit = async () => {
    setError('');
    if (!title.trim()) { setError('Give the page a title.'); return; }
    if (!effectiveRel) { setError('Give the page a URL.'); return; }
    setBusy(true);
    try {
      const page = await createSitePage(siteId, {
        page_rel: effectiveRel,
        title: title.trim(),
        kind,
        chrome_donor: donor,
        meta_title: metaTitle.trim(),
        meta_description: metaDescription.trim(),
        nav_label: navLabel.trim() || title.trim(),
        add_nav_link: addNavLink,
        nav_selector: navSelector,
        include_donor_scripts: includeScripts,
      });
      onCreated(page);
    } catch (e: any) {
      // The backend owns every rule here (duplicate URL, collision with a real bundle file, one
      // blog index per site) — surface its message rather than re-implementing the rules.
      setError(e?.response?.data?.detail || e.message || 'Could not create the page.');
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)', width: 480, maxWidth: '92%', maxHeight: '86vh',
                    display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '14px 18px', borderBottom: '1px solid var(--stroke)' }}>
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>New page</span>
          <button onClick={onClose} aria-label="Close"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '4px 18px 18px', overflowY: 'auto' }}>
          <label style={label}>Page name</label>
          <input style={input} value={title} autoFocus placeholder="e.g. Articles"
                 onChange={e => setTitle(e.target.value)} />

          <label style={label}>URL</label>
          <input style={input} value={effectiveRel} placeholder="articles.html"
                 onChange={e => { setRelTouched(true); setPageRel(e.target.value); }} />
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
            Must end in <code>.html</code> and not match a file already in your bundle.
          </div>

          <label style={label}>Page type</label>
          <select style={input} value={kind} onChange={e => setKind(e.target.value as any)}>
            <option value="landing">Landing page</option>
            <option value="blog_index" disabled={hasBlogIndex}>
              Blog index{hasBlogIndex ? ' — this site already has one' : ''}
            </option>
          </select>

          <label style={label}>Meta Title</label>
          <input style={input} value={metaTitle} placeholder={title || 'Page title for search results'}
                 onChange={e => setMetaTitle(e.target.value)} />

          <label style={label}>Meta Description</label>
          <textarea style={{ ...input, resize: 'vertical', minHeight: 60, fontFamily: 'inherit' }}
                    value={metaDescription} rows={2}
                    placeholder="A compelling description that appears in search results…"
                    onChange={e => setMetaDescription(e.target.value)} />

          <label style={label}>Copy the look from</label>
          <select style={input} value={donor} onChange={e => setDonor(e.target.value)}>
            {bundlePages.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
            The new page borrows this page’s fonts, colours, menu and footer, so it matches your site.
          </div>

          <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeScripts} style={{ accentColor: 'var(--primary-500)' }}
                   onChange={e => setIncludeScripts(e.target.checked)} />
            Include the site’s scripts (keeps the mobile menu working)
          </label>

          <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={addNavLink} style={{ accentColor: 'var(--primary-500)' }}
                   onChange={e => setAddNavLink(e.target.checked)} />
            Add a link to the site menu
          </label>

          {addNavLink && (
            <div style={{ paddingLeft: 22 }}>
              <label style={label}>Menu label</label>
              <input style={input} value={navLabel} placeholder={title || 'Blog'}
                     onChange={e => setNavLabel(e.target.value)} />
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                The link is added to every page’s menu.{' '}
                <button type="button" onClick={() => setShowNavPicker(v => !v)}
                        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer',
                                 color: 'var(--primary-500)', font: 'inherit' }}>
                  {showNavPicker ? 'Hide menu options' : 'Choose which menu'}
                </button>
              </div>
              {showNavPicker && (
                <div style={{ marginTop: 8, border: '1px solid var(--stroke)',
                              borderRadius: 'var(--radius-sm)', maxHeight: 150, overflowY: 'auto' }}>
                  <NavOption selector="" label="Detect automatically" count={null}
                             active={navSelector === ''} onPick={() => setNavSelector('')} />
                  {candidates.map(cd => (
                    <NavOption key={cd.selector} selector={cd.selector} label={cd.selector}
                               count={cd.link_count} active={navSelector === cd.selector}
                               onPick={() => setNavSelector(cd.selector)} />
                  ))}
                  {candidates.length === 0 && (
                    <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--text-secondary)' }}>
                      No menu found on this page — the page will still work, it just won’t be linked.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--danger, #dc2626)' }}>{error}</div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px',
                      borderTop: '1px solid var(--stroke)' }}>
          <GlassButton variant="secondary" onClick={onClose}>Cancel</GlassButton>
          <GlassButton variant="primary" onClick={submit} disabled={busy}>
            {busy ? 'Creating…' : 'Create page'}
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

const NavOption: React.FC<{
  selector: string; label: string; count: number | null; active: boolean; onPick: () => void;
}> = ({ label, count, active, onPick }) => (
  <button type="button" onClick={onPick}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                   padding: '7px 10px', border: 'none', cursor: 'pointer', font: 'inherit',
                   fontSize: 12, color: 'var(--text)',
                   background: active ? 'rgba(33,119,255,0.08)' : 'transparent' }}>
    <span style={{ width: 14, flexShrink: 0, color: 'var(--primary-500)' }}>
      {active && <Check size={14} />}
    </span>
    {/* preview_html is deliberately NOT rendered as markup — it is host HTML from an uploaded bundle. */}
    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    {count !== null && (
      <span style={{ fontSize: 11, color: count === 0 ? 'var(--danger, #dc2626)' : 'var(--text-secondary)' }}>
        {count === 0 ? 'no links' : `${count} links`}
      </span>
    )}
  </button>
);

export default NewPageModal;
